import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LoadingButton } from '../components/common/LoadingIndicators';
import ErrorDisplay from '../components/common/ErrorDisplay';
import { Input, Card, Spinner, GoogleIcon } from '../components/ui';
import { formatAuthError } from '../utils/errorFormatting';
import { toAppError, AppError } from '../types/api';
import Logger from '../utils/logger';

const logger = Logger.create('SignUpPage');

const SignUpPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<AppError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { signUp, signInWithGoogle, currentUser, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser && !loading) {
      navigate('/main');
    }
  }, [currentUser, loading, navigate]);

  const handleEmailSignUp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(toAppError(new Error('Passwords do not match.'), 'validation'));
      return;
    }

    if (password.length < 6) {
      setError(
        toAppError(
          new Error('Password should be at least 6 characters long.'),
          'validation'
        )
      );
      return;
    }

    if (!email.trim()) {
      setError(
        toAppError(new Error('Please enter an email address.'), 'validation')
      );
      return;
    }

    setIsLoading(true);
    try {
      await signUp(email, password);
      navigate('/login', {
        state: { message: 'Sign up successful! Please log in.' },
      });
    } catch (err) {
      logger.error('Sign up error:', err);
      setError(toAppError(err, 'auth'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await signInWithGoogle();
    } catch (err) {
      logger.error('Google sign up error:', err);
      setError(toAppError(err, 'auth'));
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center">
        <Spinner size="lg" className="text-primary-600" />
        <p className="mt-4 text-secondary-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <Card className="w-full max-w-md" variant="elevated" padding="lg">
        <h1
          id="signup-title"
          className="text-2xl font-bold text-center text-secondary-900 mb-6"
        >
          Sign Up
        </h1>

        {error && (
          <ErrorDisplay
            error={error}
            formattedError={formatAuthError(error)}
            showClose
            onClose={() => setError(null)}
            className="mb-4"
          />
        )}

        <form
          onSubmit={handleEmailSignUp}
          noValidate
          role="form"
          aria-labelledby="signup-title"
        >
          <div className="space-y-4">
            <Input
              id="email"
              label="Email Address"
              type="email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              fullWidth
            />
            <Input
              id="password"
              label="Password"
              type="password"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              fullWidth
            />
            <Input
              id="confirmPassword"
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              required
              fullWidth
            />
          </div>

          <LoadingButton
            type="submit"
            className="w-full mt-6"
            disabled={
              !email.trim() || !password.trim() || !confirmPassword.trim()
            }
            loading={isLoading}
            loadingText="Signing Up..."
            variant="primary"
            size="lg"
          >
            Sign Up
          </LoadingButton>

          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-secondary-300" />
            <span className="px-4 text-sm text-secondary-500">OR</span>
            <div className="flex-1 border-t border-secondary-300" />
          </div>

          <LoadingButton
            type="button"
            className="w-full"
            variant="outline"
            size="lg"
            leftIcon={<GoogleIcon className="w-5 h-5" />}
            onClick={handleGoogleSignUp}
            loading={isLoading}
            loadingText="Signing Up..."
            aria-label="Sign up with Google account"
          >
            Sign up with Google
          </LoadingButton>

          <div className="flex justify-center gap-2 mt-6">
            <span className="text-sm text-secondary-600">
              Already have an account?
            </span>
            <RouterLink
              to="/login"
              className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
            >
              Sign In
            </RouterLink>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default SignUpPage;
